import { http } from 'wagmi'
import { createClient } from 'viem'
import { base, baseSepolia, foundry } from 'wagmi/chains'
import { connectorsForWallets } from '@rainbow-me/rainbowkit'
import { createConfig } from 'wagmi'
import {
    metaMaskWallet,
    coinbaseWallet,
    walletConnectWallet,
    rainbowWallet,
    ledgerWallet,
    safeWallet,
} from '@rainbow-me/rainbowkit/wallets'

// Get environment variables with trim to handle any whitespace
const projectId = (import.meta.env.VITE_WALLET_CONNECT_PROJECT_ID || 'demo').trim()
const chainId = parseInt((import.meta.env.VITE_CHAIN_ID || '8453').trim())

// Test: Always use production wallet settings to see if wallets behave consistently
const isDevelopment = false;
// Commented out complex detection logic for testing:
// const isDevelopment =
//     // Check explicit environment variable
//     import.meta.env.VITE_ENVIRONMENT === 'local' ||
//     import.meta.env.NODE_ENV === 'development' ||
//     // Check if running on localhost
//     window.location.hostname === 'localhost' ||
//     window.location.hostname === '127.0.0.1' ||
//     // Check if using Anvil chain (local development)
//     chainId === 31337 ||
//     // Check if using development port
//     window.location.port === '5173' || // Vite default port
//     window.location.port === '3000';   // Common dev port

console.log(`🔧 Wallet Configuration Test: ${isDevelopment ? 'Development' : 'Production'} Mode`, {
    chainId,
    hostname: window.location.hostname,
    port: window.location.port,
    viteEnv: import.meta.env.VITE_ENVIRONMENT,
    nodeEnv: import.meta.env.NODE_ENV
});

// Secure RPC configuration - uses serverless proxy endpoints
const getSecureRpcUrl = () => {
    const baseUrl = window.location.origin;

    // For local development (Anvil fork/local)
    if (chainId === 31337) {
        // Use direct connection for Anvil (ngrok URL for fork, localhost for local)
        return import.meta.env.VITE_DEV_RPC_URL || 'http://localhost:8545';
    }

    // For production (Base mainnet) - use secure multi-RPC proxy
    return import.meta.env.VITE_NODE_RPC_URL || `${baseUrl}/api/multi-rpc-proxy`;
};

// Define chains based on environment
// IMPORTANT: First chain is the default when no wallet is connected
const chains = [base, baseSepolia, foundry] as const

// Define wallets with environment-aware Coinbase configuration
const wallets = [
    metaMaskWallet,
    walletConnectWallet,
    ledgerWallet,
    // Configure Coinbase Wallet based on environment
    (walletOptions: any) => coinbaseWallet({
        ...walletOptions,
        appName: "MEMER Operational Dashboard",
        appLogoUrl: undefined,
        // In development: disable analytics/metrics to prevent 401 errors
        // In production: enable full functionality
        ...(isDevelopment ? {
            preference: 'eoaOnly', // Use EOA only to avoid smart wallet metrics
            enableMobileWalletLink: false, // Disable mobile wallet link
        } : {
            // Production settings - enable all features
            preference: 'all', // Enable both EOA and smart wallets
            enableMobileWalletLink: true,
        })
    }),
    rainbowWallet,
    safeWallet,
];

// Simple wallet group configuration
const supportedWalletGroup = { groupName: "Supported Wallets", wallets };

// Create connectors with custom configuration
export const wagmiConnectors = connectorsForWallets([supportedWalletGroup], {
    appName: "MEMER Operational Dashboard",
    projectId,
});

// Create Wagmi config using the exact same structure as Scaffold UI
export const config = createConfig({
    chains,
    connectors: wagmiConnectors,
    ssr: false,
    client({ chain }) {
        return createClient({
            chain,
            transport: http(getSecureRpcUrl()),
            pollingInterval: 30000,
        });
    },
})

// Export types
export type Config = typeof config
